import Backbone from '@polleverywhere/backbone'
import _ from 'underscore'
import URI from 'urijs'
import subscribe from '@polleverywhere/firehose_client'
import env from '../../env'

/**
 * Copies object values from one array of objects to another array of objects
 * with matching `id` keys. Does not mutate array or objects passed in. Never
 * adds or removes target objects to the targets collection.
 * @param {Array} targets Target objects to copy to.
 * @param {Array} sources Source objects to copy from.
 * @return {Array} Resulting list of objects with values merged together. If no
 *    sources exist, then the original `targets` list is returned.
 */
function mergeObjectsById (targets = [], sources = []) {
  if (!sources.length) { return targets }

  const sourcesById = _.groupBy(sources, 'id')
  return targets.map((target, index) => {
    const groupedSources = sourcesById[target.id]
    let source = {}

    if (groupedSources) {
      // When source objects don't have IDs, we do still need to merge source
      // objects into targets. Since in this case, we cannot group sources by
      // `id`, we trust that objects are ordered correctly between both
      // target/source arrays and merge the two lists of objects together.
      source = groupedSources.length === 1
        ? groupedSources.pop()
        : groupedSources[index]
    }

    return Object.assign({}, target, source)
  })
}

/**
 * Merges options and results datasets to become one. Always gets the most
 * current set of activity options as the source of options to merge into.
 * @param {Object} data Dataset that contains options/results keys.
 * @return {Object} Mutated dataset
 */
function normalizeOptionsData (data = {}) {
  let activityOptions = []
  if (this.activity.mcpOptions) {
    activityOptions = this.activity.mcpOptions.map((option) => option.attributes)
  } else if (this.activity.regions) {
    activityOptions = this.activity.regions.map((region) => region.attributes)
  }

  if (data.options) {
    // Options may contain MCP option models instead of raw attributes. To
    // ensure merging compatitibility, convert models into simple objects.
    data.options = data.options.map((option) => {
      return option.attributes || option
    })

    // To support incoming option add, updates and removals, first create a
    // full options dataset. Then, re-merge the new values back in.
    const mergedOptions = mergeObjectsById(data.options, activityOptions)
    data.options = mergeObjectsById(mergedOptions, data.options)
  }

  if (data.results) {
    // Rename `option_id` to `id` to prepare merging it into options.
    data.results.forEach((result) => {
      if (!result.option_id) { return }
      result.id = result.option_id
      delete result.option_id
    })

    // Merge results into incoming or existing activity options.
    const options = data.options || activityOptions
    data.options = mergeObjectsById(options, data.results)
    delete data.results
  }

  // For CIPs, rename `clicks` to `results`.
  if (data.clicks) {
    data.results = data.clicks
    delete data.clicks
  }

  return data
}

/**
 * Handle live result data from Firehose.
 * @param {Object} result Firehose result data response
 * @return {Object} Returns a new dataset with result as a root key.
 */
function normalizeFirehoseResultData (result = {}) {
  // Result is delivered flat, but in order for the live response processor
  // to recognize this as a single result, we give it a key.
  return Object.keys(result).length ? { result } : result
}

/**
 * Handle live updates from Firehose by delegating it to the internal processor.
 * @param {Object} data Firehose data response
 */
function handleFirehoseData (data = {}, frame = {}) {
  if (!Object.keys(data).length) { return }

  // For live results coming in, normalize the dataset into the right format.
  const fromResultsChannel = frame.channel.includes('results')
  if (fromResultsChannel) {
    data = normalizeFirehoseResultData.call(this, data)
  }

  // Old channel processors expect the channel to be inside a `path` property.
  // This is what the old `_live` mixin passed back to the message handler.
  frame.path = frame.channel

  // Let the internal activity model handle all Firehose live updates.
  this.activity.processLiveResponse(data, frame)

  // TODO: Determine if we still need this and remove if we can.
  // Trigger a live response event so 3rd party views can access data coming
  // down from firehose.
  this.trigger('liveResponse', data)
}

/**
 * Defines private helper methods that are shared between `PresenterInstance`
 * and `ParticipantInstance` models.
 * @type {Object}
 */
export const Common = {
  /**
   * Throws an error if the specified object does not contain required keys.
   * @param {Object} attrs
   */
  requireAttrs (attrs = {}, requires = []) {
    requires.forEach((key) => {
      if (!attrs[key]) {
        throw new Error(`${key} must be provided`)
      }
    })
  },

  /**
   * Parse the specified data object and update the internal activity model.
   * @param {Object} data Serialized activity model data
   * @param {Object} options
   * @return {Object} Potentially a mutated dataset
   */
  parseActivity (data = {}, options = {}) {
    if (!Object.keys(data).length) { return }

    // Tack on attr to ensure this dataset never looks like it has a root key.
    // Otherwise, it will incorrectly be popped off by activity parsers.
    data.type = this.activity.type()

    data = this.activity.parse(data, options)
    this.activity.set(data)
    return data
  },

  /**
   * Returns the name of the key attribute for this instance. Used as a helper
   * property to access activity keys based on the instance type.
   * @return {string}
   */
  getIdAttribute () {
    return `${this.type}_key`
  },

  /**
   * Dynamically generates the API resource URL for this instance.
   * @return {string}
   */
  getUrlRoot () {
    const uri = URI('/api/')
      .segment(this.type)
      .segment(this.activity.type() + 's')
    const key = this.activity.get(this.idAttribute)
    if (key) {
      uri.segment(key)
    }
    return uri.toString()
  }
}

/**
 * Methods to be mixed into every Presenter|Participant instance model.
 * @type {Object}
 */
const MixinMethods = {
  activate () {
    const opts = {
      method: 'PUT',
      url: '/api/activation',
      data: {
        type: this.activity.type(),
        uid: this.activity.get(this.idAttribute)
      }
    }
    return Backbone.ajax(opts).done(() => {
      this.activity.set(this.normalizeActivityData({ activated: true }))
    })
  },

  deactivate () {
    const opts = {
      method: 'DELETE',
      url: '/api/activation',
      data: {
        user_id: this.activity.presenter.get('id')
      }
    }
    return Backbone.ajax(opts).done(() => {
      this.activity.set(this.normalizeActivityData({ activated: false }))
    })
  },

  /**
   * Subscribe to firehose channels. Always handle messages and use multiplex.
   * @param {Object} options See `live()` method for details.
   * @return {Promise} Subscribe promise
   */
  start (options = {}) {
    // If channel subscriptions already exist, don't subscribe again.
    if (this.subscriptions.length) { return }

    this.subscriptions = this.channels.map((path) => {
      const subscription = subscribe(path, {
        host: env.get('firehose_url').toString(),
        ssl: env.get('firehose_ssl')
      })
      subscription.on('message', handleFirehoseData.bind(this))
      return subscription
    })
  },

  /**
   * Unsubscribe from firehose channels.
   * @return {Promise} Unsubscribe promise
   */
  stop () {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe())
    this.subscriptions = []
  },

  /**
   * Mutates serialized activity data into a standard format that all activity
   * models expect. Fills in legacy attrs that existed on old API endpoints.
   * @param {Object} data Incoming data either from Firehose or instance API.
   * @return {Object} Normalized data
   */
  normalizeActivityData (data = {}) {
    if ('shared_key' in data) {
      data.permalink = data.shared_key
    }

    if ('activated' in data) {
      data.current = data.activated
      data.presenter = Object.assign({}, data.presenter, {
        active: data.activated
      })
    }

    // Backfill owner attrs from the presenter object only if owner ID doesn't
    // already exist but the presenter object does.
    if (!data.owner_id && data.presenter && data.presenter.id) {
      data.owner_id = data.presenter.id
      data.owner_login = data.presenter.login
      data.owner_name = data.presenter.name
      data.owner = { id: data.presenter.id }
    }

    // For MCP/CIPs only, normalize options and results data.
    const type = data.type || this.activity.type()
    if (/multiple|ranking|clickable/i.test(type)) {
      data = normalizeOptionsData.call(this, data)
    }

    // Normalize all MQA subpoll instances.
    if (data.poll_instances) {
      data.polls = data.poll_instances
      delete data.poll_instances
      data.polls.forEach((poll) => this.normalizeActivityData(poll))
    }

    return data
  }
}

/**
 * Properties to be mixed into every Presenter|Participant instance model.
 * @type {Object}
 */
const MixinProperties = {
  /**
   * Holds reference to all firehose channels currently subscribed to.
   * @type {Array}
   */
  subscriptions: {
    writable: true,
    value: []
  },

  /**
   * Accesor to the internal activity model.
   * @type {Poll|Survey|Competition}
   */
  activity: {
    get () {
      return this._activity
    },

    set (model) {
      if (!model) {
        throw new Error(`${this.type} instance activity must be defined.`)
      }

      // Support legacy consumers constructing activities with permalinks by
      // mapping it to the appropriate keys.
      const permalink = model.get('shared_key') || model.get('permalink')
      if (permalink) {
        // Permalink and shared key are analagous.
        model.set('shared_key', permalink)

        // When presenter key is not provided, use permalink instead and it
        // will self-correct itself with the real key after hydrating.
        if (!model.has(this.idAttribute)) {
          model.set(this.idAttribute, permalink)
        }
      }

      // Create two-way reference btwn presenter instance and its activities.
      model.instance = this

      this._activity = model
    }
  }
}

/**
 * Mixin function that in addition to returning an object of methods, also
 * defines class properties against every instance.
 * @return {Object}
 */
export default function (self) {
  Object.defineProperties(self, MixinProperties)
  return MixinMethods
}
